import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ApiFactory from "neon-frontend-utils/src/api/factory";
import AppConfig from "../../../../App.config";
import RedirectRenderer from "../../../../utils/RedirectRenderer";
import MenuActions from "../../constants/MenuActions";
import TextInCircle from "./../../../../components/TextInCircle/TextInCircle";
import NavigationItem from "./components/NavigationItem/NavigationItem";
import style from "./style/menu.scss";

const LOGO_PATH = "/assets/images/logo-ltp-rgb.svg";
const LTP_SETTINGS_PATH = "ltpSettings";

const Menu = ({
    menuItems,
    subMenuItems,
    selectedMenuNode,
    user,
    showModalToSwitchOrganisation,
    navigateToRoute,
    i18n,
    driverObj,
}) => {
    const api = ApiFactory.get("neon");
    const getNavigationItem = (route) => (
        <NavigationItem
            key={route.label}
            classes={{
                root: route.component === LTP_SETTINGS_PATH ? style.ltpSettings : "", // ltpSettings item has custom css
            }}
            iconType={route.iconType}
            id={route.component}
            label={i18n[`cp_menu_${route.label.toLowerCase()}`] || route.label}
            link={route.link}
            navigateToRoute={navigateToRoute}
            selected={selectedMenuNode.includes(route.link)}
        />
    );

    let name, initials;

    if (user) {
        name = `${user.firstName} ${user.lastName}`;
        initials = name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();
    }

    const navigationItems = [];
    const subNavigationItems = [];

    menuItems.forEach((route) => {
        if (
            api.getAuthoriser().authorise(api.getAuthenticator().getUser(), route.component, MenuActions.ROUTE_ACTION)
        ) {
            navigationItems.push(getNavigationItem(route));
        }
    });

    subMenuItems.forEach((subRoute) => {
        if (selectedMenuNode.includes(subRoute.parent)) {
            if (
                api
                    .getAuthoriser()
                    .authorise(api.getAuthenticator().getUser(), subRoute.component, MenuActions.ROUTE_ACTION)
            ) {
                subNavigationItems.push(getNavigationItem(subRoute));
            }

            // automatically go to first item of the constructed (thus authorised) sub menu items, if there is only one item in it
            if (subMenuItems.length === 1 && !selectedMenuNode.includes(subNavigationItems[0].props.link)) {
                navigateToRoute(subNavigationItems[0].props.link);
            }
        }
    });

    const getGuidedTourButton = () => {
        const homeItem = AppConfig.menuItems.find((item) => item.component === "home");

        if (selectedMenuNode !== homeItem?.link) {
            return null;
        }

        if (!api.getAuthoriser().authorise(api.getAuthenticator().getUser(), "menu", "viewMenu")) {
            return null;
        }

        return (
            <div className={style.menuNavigationTour}>
                <TextInCircle
                    action={() => {
                        driverObj.drive();
                    }}
                    classes={{ root: style.textInCircle__tour }}
                    cyReference={"tour-button"}
                >
                    ?
                </TextInCircle>
            </div>
        );
    };

    return (
        <div className={style.menu}>
            <ul className={style.menuNavigation}>
                <li className={style.menuNavigationLogo}>
                    <img alt="logo" src={LOGO_PATH} />
                </li>
                {navigationItems}
            </ul>

            <div className={style.menuNavigationUserActions}>
                {user && user.isTenantUser && user.hasMultipleTenants ? (
                    <div
                        className={style.menuNavigationSelectOrganisation}
                        role="button"
                        tabIndex="1"
                        onClick={() => {
                            showModalToSwitchOrganisation(false);
                        }}
                    >
                        <FontAwesomeIcon icon={"sitemap"} />
                    </div>
                ) : null}
                {getGuidedTourButton()}
                <div
                    className={`${style.menuNavigationProfile} ${
                        selectedMenuNode.includes(AppConfig.routes.profile) ? style.menuNavigationProfile__active : ""
                    }`}
                >
                    {user && Object.keys(user).length ? (
                        <TextInCircle
                            action={() => {
                                RedirectRenderer.render(AppConfig.routes.profile, true, false);
                            }}
                            classes={{ root: style.textInCircle__profile }}
                            cyReference={"profile-button"}
                        >
                            {initials}
                        </TextInCircle>
                    ) : null}
                </div>
            </div>
            <ul className={style.menuSubNavigation}>{subNavigationItems}</ul>
        </div>
    );
};

Menu.propTypes = {
    i18n: PropTypes.object,
    menuItems: PropTypes.array,
    navigateToRoute: PropTypes.func,
    selectedMenuNode: PropTypes.string,
    showModalToSwitchOrganisation: PropTypes.func,
    subMenuItems: PropTypes.array,
    user: PropTypes.object,
};

export default connect()(Menu);
